import { TextField, InputAdornment, IconButton, SxProps, Theme, Tooltip } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import { useRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface FileInputProps {
  value: File | null;
  onChange: (file: File | null) => void;
  label?: string;
  accept?: string;
  sx?: SxProps<Theme>;
  clearButtonTitle?: string;
  disabled?: boolean;
  error?: boolean;
  maxSize?: number; // in bytes
  onError?: (error: string) => void;
  fullWidth?: boolean;
}

export function FileInput({
  value,
  onChange,
  label,
  accept = 'image/*', // Accepts all image types
  sx,
  clearButtonTitle,
  disabled = false,
  error = false,
  maxSize = 10 * 1024 * 1024, // 10MB default
  onError,
  fullWidth = true,
}: FileInputProps) {
  const { t } = useTranslation(['support']);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [validationError, setValidationError] = useState<string>('');

  const validateFile = useCallback(
    (file: File): boolean => {
      if (maxSize && file.size > maxSize) {
        const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(2);
        const errorMsg = t('support:fileSizeExceeds', { maxSize: maxSizeMB });
        setValidationError(errorMsg);
        onError?.(errorMsg);
        return false;
      }

      if (accept) {
        const acceptedTypes = accept.split(',').map((type) => type.trim().toLowerCase());
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
        const mimeType = file.type.toLowerCase();

        const isValidExtension = acceptedTypes.some((type) => type === fileExtension);
        const isValidMimeType = acceptedTypes.some((type) => {
          if (type.startsWith('.')) {
            return false;
          }
          return mimeType.includes(type.replace('*', ''));
        });

        if (!isValidExtension && !isValidMimeType) {
          const errorMsg = t('support:invalidFileType');
          setValidationError(errorMsg);
          onError?.(errorMsg);
          return false;
        }
      }

      setValidationError('');
      return true;
    },
    [maxSize, accept, onError, t]
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        if (validateFile(file)) {
          onChange(file);
        } else {
          // Clear previous file and reset input if validation fails
          onChange(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      }
    },
    [onChange, validateFile]
  );

  const handleClearFile = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onChange(null);
      setValidationError('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [onChange]
  );

  const handleTextFieldClick = useCallback(() => {
    if (!value && !disabled) {
      fileInputRef.current?.click();
    }
  }, [value, disabled]);

  const handleAttachClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  const getDisplayValue = useCallback(() => {
    if (!value) {
      return '';
    }
    const fileSizeMB = (value.size / (1024 * 1024)).toFixed(2);
    return `${value.name} (${fileSizeMB}MB)`;
  }, [value]);

  return (
    <TextField
      fullWidth={fullWidth}
      sx={{
        cursor: !value && !disabled ? 'pointer' : 'default',
        '& .MuiInputBase-input': {
          cursor: !value && !disabled ? 'pointer' : 'default',
        },
        ...sx,
      }}
      label={label}
      value={getDisplayValue()}
      onClick={handleTextFieldClick}
      disabled={disabled}
      error={error || !!validationError}
      helperText={validationError}
      slotProps={{
        input: {
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              {value && !disabled && (
                <Tooltip title={clearButtonTitle || t('support:remove')}>
                  <IconButton onClick={handleClearFile} edge="end" size="small" aria-label="clear file">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={t('support:attachFile') || 'Attach file'}>
                <span>
                  <IconButton
                    component="label"
                    edge="end"
                    disabled={disabled}
                    onClick={handleAttachClick}
                    aria-label="attach file"
                  >
                    <AttachFileIcon />
                    <input
                      ref={fileInputRef}
                      type="file"
                      hidden
                      accept={accept}
                      onChange={handleFileChange}
                      disabled={disabled}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </InputAdornment>
          ),
        },
      }}
    />
  );
}
